// Root authenticated layout — Console Glass shell (task 355.4): floating navigation panel, sticky glass
// top bar (breadcrumb, ⌘K, environment, tasks), scrollable content, and the command palette.

import React, { Suspense, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useSidebarStore } from '../../../stores/sidebarStore';
import { useTokenRefresh } from '../../../features/auth/hooks/useTokenRefresh';
import { useSessionPermissions } from '../../../features/auth/hooks/useSessionPermissions';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { PrivacyBanner } from '../../privacy/PrivacyToggle';
import { usePrivacyShortcut } from '../../privacy/usePrivacyShortcut';
import { usePrivacyCatalogLoader } from '../../privacy/usePrivacy';
import CommandPalette from './CommandPalette';
import LoadingOverlay from '../common/LoadingOverlay';
import StatusGroupsProvider from '../../theme/StatusGroupsProvider';
import styles from './Shell.module.css';

/** Below this width the navigation collapses to its icon rail (the old Sider's `md` breakpoint). */
const COLLAPSE_QUERY = '(max-width: 767px)';

function AppLayout(): React.ReactElement {
  const { collapsed, setCollapsed } = useSidebarStore();
  const [paletteOpen, setPaletteOpen] = useState(false);
  useTokenRefresh();
  // Task 326.17 (Q27): permission codes come from GET api/console/me, not from the JWT.
  useSessionPermissions();
  // Task 402.10: the shortcut, and the classification vocabulary loaded once for the whole console.
  usePrivacyShortcut();
  usePrivacyCatalogLoader();

  // Collapse on narrow screens; the persisted preference is kept on wide ones.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(COLLAPSE_QUERY);
    if (mql.matches) setCollapsed(true);
    const onChange = (e: MediaQueryListEvent) => setCollapsed(e.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [setCollapsed]);

  // ⌘K / Ctrl+K toggles the command palette anywhere in the console.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <StatusGroupsProvider>
      <div className={styles.shell}>
        <Sidebar />
        <div className={[styles.main, collapsed ? styles.mainCollapsed : ''].join(' ')}>
          <TopBar onOpenPalette={() => setPaletteOpen(true)} />
          <PrivacyBanner />
          <main className={styles.content}>
            <Suspense fallback={<LoadingOverlay />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      </div>
    </StatusGroupsProvider>
  );
}

export default AppLayout;
